/**
 * Sesion de demostracion.
 *
 * Unica fuente de verdad para responder "¿estoy en modo demo?". Todo lo demas
 * -repositorios, contextos, interfaz- pregunta aqui.
 *
 * ── La garantia de aislamiento ──────────────────────────────────────────────
 *
 * La sesion demo NO tiene token de Cognito. Esa es la propiedad que hace
 * imposible que toque datos reales, y no depende de que el codigo se comporte
 * bien: aunque una llamada se escapara hacia la API real, API Gateway la
 * rechazaria con 401 porque su autorizador exige un token valido en la cabecera
 * Authorization.
 *
 * Dicho de otro modo: el aislamiento no es una convencion que respetamos, es
 * una imposibilidad tecnica.
 *
 * En sentido contrario, `isDemoSession()` exige que NO exista un token real.
 * Un usuario autenticado de verdad nunca vera datos simulados, aunque quedara
 * un marcador de demo olvidado en el navegador.
 */

const DEMO_FLAG = 'nissaguard.demo.active';

/** Datos que definen al usuario de demostracion. */
export const DEMO_USER = {
  email: 'demo@nisaguard.com',
  name: 'Cuenta NisaGuard',
  sub: 'demo-session-local',
  'cognito:groups': ['Demo', 'Operators'],
};

/**
 * Credenciales de acceso a la demostración: el ÚNICO camino de entrada.
 *
 * Desde que se quitó el botón de la pantalla de login, esto es lo que separa a
 * quien puede ver el modo de prueba de quien no. `authService.login()` las
 * intercepta antes de llegar a Cognito, así que esta cuenta no existe en el
 * grupo de usuarios y nunca llega a emitir un token.
 *
 * ATENCIÓN A LO QUE ESTO ES Y LO QUE NO ES. La contraseña viaja en el paquete
 * de JavaScript que se descarga el navegador: quien abra las herramientas de
 * desarrollo y busque, la encuentra. Es un freno para quien pase por delante,
 * no un secreto. Lo que sí es sólido es lo de después: la sesión no emite token
 * de Cognito, así que aunque alguien entre no puede leer ni tocar datos reales
 * -API Gateway rechaza cualquier llamada con un 401-. El aislamiento no depende
 * de esta contraseña.
 *
 * PARA CAMBIARLA: basta con la línea de abajo. Si algún día conviene sacarla
 * del código, el sitio es `process.env.REACT_APP_DEMO_PASSWORD` con este valor
 * como respaldo.
 */
export const DEMO_CREDENTIALS = {
  email: DEMO_USER.email,
  password: 'NissaDemo2026',
};

/** Lee el token real sin depender de authService, para evitar un ciclo de imports. */
function hasRealToken() {
  try {
    return Boolean(localStorage.getItem('idToken'));
  } catch {
    return false;
  }
}

/**
 * ¿La sesion actual es de demostracion?
 *
 * Las dos condiciones son necesarias: haber iniciado la demo y NO tener una
 * sesion real. Si ambas coincidieran, mandaria la real.
 */
export function isDemoSession() {
  try {
    return localStorage.getItem(DEMO_FLAG) === 'true' && !hasRealToken();
  } catch {
    return false;
  }
}

/** Inicia la demo. Se rechaza si ya hay una sesion real: nunca las dos a la vez. */
export function startDemoSession() {
  if (hasRealToken()) {
    throw new Error('Cierra la sesión actual antes de continuar');
  }
  localStorage.setItem(DEMO_FLAG, 'true');
}

/**
 * Termina la demo y borra sus datos.
 *
 * El borrado importa: sin el, los cambios hechos durante la demostracion
 * -dispositivos vinculados, planes contratados- reaparecerian en la siguiente,
 * y quien entrara despues veria un sistema en un estado que no reconoce.
 */
export function endDemoSession() {
  localStorage.removeItem(DEMO_FLAG);
  try {
    // Todo el estado simulado vive bajo este prefijo, nunca mezclado con el real.
    Object.keys(localStorage)
      .filter((k) => k.startsWith('nissaguard.mock.'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* sin acceso a localStorage no hay nada que limpiar */
  }
}

/**
 * AUTORREPARACION al cargar la aplicacion.
 *
 * Si conviven un token real y la marca de demostracion, gana el token y la
 * marca se borra junto con sus datos. `isDemoSession()` ya devolveria `false`
 * en esa situacion, pero dejar estado simulado en el navegador de alguien que
 * mira datos reales es pedir un problema: basta con que cierre sesion para que
 * la demo reviva sola.
 *
 * Se ejecuta una vez, al importar el modulo.
 */
(function repararEstadoInconsistente() {
  try {
    if (hasRealToken() && localStorage.getItem(DEMO_FLAG) === 'true') {
      console.warn('[NissaGuard] Estado de demostración residual detectado con sesión real activa. Se limpia.');
      endDemoSession();
    }
  } catch {
    /* sin localStorage no hay nada que reparar */
  }
})();

/** Comprueba unas credenciales contra las de la demo. */
export function matchesDemoCredentials(email, password) {
  return (
    String(email || '').trim().toLowerCase() === DEMO_CREDENTIALS.email &&
    String(password || '') === DEMO_CREDENTIALS.password
  );
}
